import { useState } from "react"
import cn from "@/v2-ui/utils/utils.cn"
import Tabs from "@/v2-backoffice-client/components/tabs"
import Page, { type PageProps } from "@/v2-backoffice-client/components/page"
import SignInUsersTab from "@/v2-backoffice-client/signIn/SignInUsersTab"
import SignInEventsTab from "@/v2-backoffice-client/signIn/SignInEventsTab"
import SignInSessionsTab from "@/v2-backoffice-client/signIn/SignInSessionsTab"
import UsageTable from "@/v2-backoffice-client/usage/UsageTable"
import UsageLicenseDeniedFeedTable from "@/v2-backoffice-client/usage/denied/UsageLicenseDeniedFeedTable"
import PortfolioTab from "@/v2-backoffice-client/portfolio/PortfolioTab"
import LicenseTrialReport from "@/v2-backoffice-client/license/trials/LicenseTrialReport"
import type { Period } from "@/v2-backoffice-client/signIn/signIn.period"
import {
  ACTIVITY_TABS_ID,
  ACTIVITY_VIEW_EVENTS,
  ACTIVITY_VIEW_PORTFOLIO,
  ACTIVITY_VIEW_REFUSALS,
  ACTIVITY_VIEW_SESSIONS,
  ACTIVITY_VIEW_TRIALS,
  ACTIVITY_VIEW_USAGE,
  ACTIVITY_VIEW_USERS,
} from "@/v2-backoffice-client/signIn/signIn.constants"

export type SignInPageProps = PageProps

// Activity page shell (formerly "Sign-ins"): owns only the selected period
// (shared by the sign-in tabs) and the tab layout. Each tab owns its own data
// fetching, so only the active tab's query runs and no tab juggles more than
// its single data concern. Portfolio (the book-of-business rollup) lives
// here as the highest-level lens on the same activity data.
function SignInPage(props: SignInPageProps) {
  const { className, ...pageProps } = props

  const [period, setPeriod] = useState<Period>("7d")

  return (
    <Page className={cn("sign-in-page", className)} {...pageProps}>
      <Page.Header>
        <Page.Title>Activity</Page.Title>
      </Page.Header>

      <Page.Content className="flex-1 min-h-0 overflow-auto">
        {/* Features-board layout: the nav card beside the content. The
            Portfolio tab pins its chrome and scrolls internally; the other
            tabs keep natural height and fall back to the page scroller. No
            counts in the nav on purpose: each tab owns a heavy dashboard
            query that should only run while its tab is open. */}
        <Tabs
          id={ACTIVITY_TABS_ID}
          defaultValue={ACTIVITY_VIEW_USERS}
          variant="sidebar"
          className="min-h-0 flex-1"
        >
          <Tabs.List>
            <Tabs.Trigger value={ACTIVITY_VIEW_PORTFOLIO} iconSrc="briefcase">
              Portfolio
            </Tabs.Trigger>
            {/* The trials report: every trialing customer and whether the
                people on it use it — the weekly trial update as a page. */}
            <Tabs.Trigger value={ACTIVITY_VIEW_TRIALS} iconSrc="hourglass">
              Trials
            </Tabs.Trigger>
            <Tabs.Trigger value={ACTIVITY_VIEW_USERS} iconSrc="users">
              Users
            </Tabs.Trigger>
            <Tabs.Trigger value={ACTIVITY_VIEW_USAGE} iconSrc="pulse">
              Usage
            </Tabs.Trigger>
            {/* The refusals feed: who tried to get in and could not, at the
                stored grain. The Portfolio rolls the same rows up per
                customer; this is the list to watch. */}
            <Tabs.Trigger value={ACTIVITY_VIEW_REFUSALS} iconSrc="door-closed">
              Refused attempts
            </Tabs.Trigger>
            <Tabs.Trigger value={ACTIVITY_VIEW_SESSIONS} iconSrc="user-key">
              Active logins
            </Tabs.Trigger>
            <Tabs.Trigger value={ACTIVITY_VIEW_EVENTS} iconSrc="list-timeline">
              Sign-in Events
            </Tabs.Trigger>
          </Tabs.List>

          <Tabs.Content
            value={ACTIVITY_VIEW_PORTFOLIO}
            className="min-h-0 flex-1 overflow-hidden"
          >
            <PortfolioTab />
          </Tabs.Content>

          <Tabs.Content value={ACTIVITY_VIEW_TRIALS} className="min-h-0">
            <LicenseTrialReport />
          </Tabs.Content>

          <Tabs.Content value={ACTIVITY_VIEW_USERS} className="min-h-0">
            <SignInUsersTab period={period} onPeriodChange={setPeriod} />
          </Tabs.Content>

          <Tabs.Content value={ACTIVITY_VIEW_USAGE} className="min-h-0">
            <UsageTable />
          </Tabs.Content>

          <Tabs.Content value={ACTIVITY_VIEW_REFUSALS} className="min-h-0">
            <UsageLicenseDeniedFeedTable />
          </Tabs.Content>

          <Tabs.Content value={ACTIVITY_VIEW_SESSIONS} className="min-h-0">
            <SignInSessionsTab />
          </Tabs.Content>

          <Tabs.Content value={ACTIVITY_VIEW_EVENTS} className="min-h-0">
            <SignInEventsTab period={period} onPeriodChange={setPeriod} />
          </Tabs.Content>
        </Tabs>
      </Page.Content>
    </Page>
  )
}

export default SignInPage
